import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { queryKey } from 'constants/queryKey';
import PostApi from 'api/post';
import Skeleton from 'react-loading-skeleton';
import { Alert } from '@kontentino/ui';
import PostRevisionsListItem from './postRevisions/PostRevisionsListItem';

type Props = {
  postId: number;
};

const PostRevisions: FC<Props> = ({ postId }) => {
  const { t } = useTranslation();

  const {
    data: revisionsData,
    isLoading,
    isError,
  } = useQuery(queryKey.postRevisions({ postId }), () =>
    PostApi.getPostRevisions(postId),
  );

  const currentRevisionData = revisionsData
    ? revisionsData[revisionsData?.length - 1]
    : null;

  function renderRevisionLabel(label: string) {
    return (
      <p className="tw-mb-3  tw-text-md tw-font-semibold">
        {isLoading ? <Skeleton width={100} /> : label}
      </p>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-6 tw-py-4">
      {isError ||
        (!revisionsData && !isLoading && (
          <Alert variant="danger" className="tw-mt-6">
            {t('somethingWentWrong')}
            <p>{t('weWereUnablToLoadPreviewOfYourPost')}</p>
          </Alert>
        ))}

      <div>
        {renderRevisionLabel(t('current'))}
        <div className="tw-flex tw-flex-col tw-space-y-3 ">
          {isLoading && <Skeleton />}

          {revisionsData && currentRevisionData && (
            <PostRevisionsListItem
              revisionPostData={{
                ...currentRevisionData,
                revision: revisionsData.length,
              }}
              isCurrent={true}
              currentPostData={{
                id: postId,
                revision: revisionsData.length,
                createdAt: currentRevisionData.createdAt,
              }}
            />
          )}
        </div>
      </div>

      {currentRevisionData && revisionsData && revisionsData.length > 1 && (
        <div>
          {renderRevisionLabel(t('older'))}

          <div className="tw-flex tw-flex-col tw-space-y-3 ">
            {isLoading && <Skeleton />}

            {revisionsData
              .slice(0, -1)
              .reverse()
              .map((revision, index: number) => (
                <PostRevisionsListItem
                  key={`revision-${revision.id}`}
                  revisionPostData={{
                    ...revision,
                    revision: revisionsData.length - (index + 1),
                  }}
                  isCurrent={false}
                  currentPostData={{
                    id: postId,
                    revision: revisionsData.length,
                    createdAt: currentRevisionData.createdAt,
                  }}
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PostRevisions;
